import React, { useState } from "react"
import PropTypes from "prop-types"
import { Modal, Button, Alert } from "components/common"
import { useDispatch, useSelector } from "react-redux"
import { deleteTemplatePolicy } from "redux/templatePoliciesSlice"
import { FaExclamationTriangle, FaTrash } from "react-icons/fa"

const TemplatePolicyDeleteConfirm = ({ show, onHide, onSuccess, asset }) => {
  const dispatch = useDispatch()
  const { loading } = useSelector((state) => state.template_policies)
  const isFolder = asset?.asset_type === "folder"
  const [error, setError] = useState(null)

  const handleDelete = async () => {
    setError(null)
    try {
      // If no asset.id, this is a bulk delete - call onSuccess directly
      if (!asset.id) {
        await onSuccess()
        onHide()
        return
      }

      // Single item delete
      await dispatch(deleteTemplatePolicy(asset.id)).unwrap()
      onSuccess()
      onHide()
    } catch (err) {
      // Error is already shown via toast from Redux, but we can also display it here
      const errorMessage = err?.message || err?.error || 'Failed to delete item'
      setError(errorMessage)
    }
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      title={(
        <>
          <FaExclamationTriangle className="tw-mr-2 tw-text-yellow-500" />
          Confirm Delete
        </>
      )}
      footer={(
        <>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={loading}>
            <FaTrash className="tw-mr-2" />
            {loading ? "Deleting..." : "Delete Permanently"}
          </Button>
        </>
      )}
    >
      {error && (
        <Alert type="error" className="tw-mb-3">
          {error}
        </Alert>
      )}
      <Alert type="error" title="Warning: This action cannot be undone!">
        <p className="tw-mb-0">
          Are you sure you want to permanently delete{" "}
          <strong>{asset?.name}</strong>?
        </p>
        {isFolder && (
          <p className="tw-mb-0 tw-mt-2">
            <small>
              <em>
                All files and subfolders within this folder will also be
                deleted.
              </em>
            </small>
          </p>
        )}
      </Alert>
    </Modal>
  )
}

TemplatePolicyDeleteConfirm.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  // An asset without id means a bulk delete driven by onSuccess.
  asset: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    asset_type: PropTypes.string
  }).isRequired
}

export default TemplatePolicyDeleteConfirm
